import { datadogRum } from '@datadog/browser-rum'
import { Box } from 'core/components/Box'
import Base, { BrowserPrintState } from 'core/pages/Encoding/BrowserPrint/BrowserPrintEncode'
import { getNfcReadConfiguration } from 'stylewhere/shared/nfcAuthentication'
import { EncodingValidationRequest, IdentifierType, TmrTag } from 'stylewhere/api'
import { Text } from 'stylewhere/components'
import { EncodingExtensions } from 'stylewhere/extensions'
import { AppStore, OperationReadingProps, RfidReader, Router } from 'stylewhere/shared'
import { T, __ } from 'stylewhere/shared/i18n'
import {
  askUserConfirmation,
  getAskUserInputEveryTime,
  isExpectOnlyMandatoryIdentifiers,
  showToastError
} from 'stylewhere/shared/utils'

export interface LVCustomBrowserPrintState extends BrowserPrintState {
  lastPairedSerial: string
}

export default class LVCustomBrowserPrintEncode extends Base<
  OperationReadingProps<LVCustomBrowserPrintState>,
  LVCustomBrowserPrintState
> {
  isWrongTag = false

  state: LVCustomBrowserPrintState = {
    lastPairedSerial: '',
    ...this.state
  } as LVCustomBrowserPrintState

  async componentDidMount() {
    await super.componentDidMount()
    const lastPairedSerial = localStorage.getItem(
      `rfid-station::op_${this.operation.id}_custom_lvBrowserPrintLastPairedSerial`
    )
    if (lastPairedSerial) {
      this.setState({ lastPairedSerial: lastPairedSerial })
    }
  }

  getAntennaCustomCommands = () => {
    return EncodingExtensions.getAntennaCustomCommandsOperationType(this.operation)
  }

  startAntenna = async () => {
    if (!RfidReader.isReading()) {
      this.setState({ starting: true })
      RfidReader.setNfcReadConfiguration(getNfcReadConfiguration(this.operation))
      await RfidReader.start({ onStop: () => {}, customCommandsOperationType: this.getAntennaCustomCommands() })
    }
  }

  startReader = async () => {
    try {
      this.isWrongTag = false
      await RfidReader.stopWsCustomCommands()
      RfidReader.setNfcReadConfiguration(getNfcReadConfiguration(this.operation))
      await RfidReader.start({ onStop: () => {}, customCommandsOperationType: this.getAntennaCustomCommands() })
    } catch (error) {
      if (!AppStore.getEmulation()) {
        showToastError(
          (error as any).message ?? __(T.error.rfid_reader_initialization),
          __(T.error.error),
          this.isModal
        )
      }
    }
  }

  attachAutomaticReaderStop = () => {
    if (this.operation.autostopAntennaTimeout > 0) {
      this.clearTimerReader()
      this.timerReader = setTimeout(async () => {
        this.setState({ associationStatus: 'ERROR' })
        await this.stopAntenna()
        if (this.checkIfForceCreate('yes')) {
          this.isEncoding = true
          this.setState(
            { forceAssociation: true, pin: '', encodingCreateCounter: this.state.encodingCreateCounter + 1 },
            this.create
          )
        } else if (this.checkIfForceCreate('withUserConfirmation')) {
          const { encodingValidationResponse } = this.state
          const errors =
            encodingValidationResponse && encodingValidationResponse.errors ? encodingValidationResponse.errors : []
          // if only error with code MISSING_OPTIONAL_TAG and isExpectOnlyMandatoryIdentifiers do force create
          if (
            errors.length == 1 &&
            errors[0].errorCode === 'MISSING_OPTIONAL_TAG' &&
            isExpectOnlyMandatoryIdentifiers(this.operation)
          ) {
            this.call_force_encoding(false)
          }
          // else {
          //   const ok = await askUserConfirmation(
          //     __(T.misc.attention),
          //     this.getWithUserConfirmationMessage(errors, encodingValidationResponse)
          //   )
          //   if (ok) {
          //     this.call_force_encoding(false)
          //   } else {
          //     this.clear()
          //     this.isEncoding = false
          //   }
          // }
          // } else {
          // this.setState({ encodingCreateCounter: this.state.encodingCreateCounter + 1 }, () => this.create(true))
        }
      }, this.operation.autostopAntennaTimeout)
    }
  }

  onStopCallbackIdentifierCheck(tmpEncoding: EncodingValidationRequest) {
    // uhf: wrong tag
    if (tmpEncoding && tmpEncoding.identifiers && this.isWrongTag) {
      tmpEncoding.identifiers.forEach((element) => {
        if (element.identifierType === 'UHF_TAG') {
          element._status = 'ERROR'
          element._error = __(T.error.wrong_tag)
        } else if (element.identifierType === 'NFC_TAG' && !element._status) {
          element._status = 'SKIPPED'
          // element._error = __(T.imbustatrice.dynamic_tag_missing, { role: element.role })
        }
      })
      return false
    }

    // nfc: collision, auth timeout
    if (
      tmpEncoding &&
      tmpEncoding.identifiers &&
      (tmpEncoding.identifiers.some((idt) => idt._status === 'NFC_COLLISION') ||
        tmpEncoding.identifiers.some(
          (idt) => idt._status === 'ERROR' && idt._error && [__(T.error.authentication_timeout)].includes(idt._error)
        ))
    ) {
      tmpEncoding.identifiers.forEach((element) => {
        if (!element._status) element._status = 'SKIPPED'
      })
      return false
    }

    // default
    return (
      tmpEncoding && tmpEncoding.identifiers && !tmpEncoding.identifiers.every((idt) => idt._status === 'CONFIRMED')
    )
  }

  additionalOnTagReadCheck = (tag: TmrTag) => {
    const { encodingValidation, encodingValidationResponse, targetTag } = this.state

    if (this.isWrongTag) {
      if (tag.epc) console.log('UHF discard by wrong tag', tag.epc)
      return false
    }

    if (
      encodingValidation?.identifiers.some(
        (id) => id.identifierType === 'UHF_TAG' && id._status === 'ERROR' && id._error === __(T.error.wrong_tag)
      )
    ) {
      console.log('Ignoring tag', tag.uid ?? tag.epc, 'due to wrong tag error')
      this.setState({ processing: false })
      return false
    }

    if (
      tag.uid &&
      !encodingValidationResponse?.configuration?.details.some((conf) => conf.identifierType === 'NFC_TAG')
    ) {
      this.setState({ processing: false })
      return false
    }

    if (encodingValidation && tag.epc && tag.epc !== targetTag) {
      if (
        encodingValidation.identifiers.some(
          (id) => id.identifierType === 'UHF_TAG' && id.code === targetTag && id._status === 'CONFIRMED'
        )
      ) {
        console.log('UHF discard already confirmed', tag.epc)
        return false
      }

      this.isWrongTag = true
      console.log('UHF wrong tag', tag.epc)
      this.setState({ processing: false, associationStatus: 'ERROR' })
      showToastError(__(T.error.wrong_tag), __(T.error.error), this.isModal)
      void this.stopAntenna()
      this.clearTimerReader()
      if (this.datadogSession) {
        this.datadogSession.errors?.push({
          error: 'wrongUhfTagRead',
          details: { expected: targetTag, read: tag.epc }
        })
        datadogRum.addAction('err_wrongUhfTagRead', this.datadogSession)
      }
      return false
    }

    return true
  }

  onTagReadValidate = async () => {
    try {
      this.isEncoding = true
      console.log('Validating encoding', this.state.encodingValidation)
      const res = await EncodingExtensions.validate(this.state.encodingValidation!)
      if (
        this.state.encodingValidation?.identifiers.some(
          (id) => id.identifierType === 'UHF_TAG' && id._status === 'ERROR' && id._error === __(T.error.wrong_tag)
        )
      ) {
        console.log('Ignoring tags due to wrong tag error')
        return
      }
      if (
        this.state.encodingValidation?.identifiers.some(
          (id) =>
            id.identifierType === 'NFC_TAG' &&
            id._status === 'ERROR' &&
            id._error === __(T.error.authentication_timeout)
        )
      ) {
        console.log('nfc authentication timeout')
        if (this.datadogSession) {
          this.datadogSession.errors?.push({
            error: 'nfcAuthTimeout',
            details: { nfcAuthTimeoutOffsetMs: Date.now() - this.datadogSession.startTime.getTime() }
          })
          datadogRum.addAction('err_nfcAuthTimeout', this.datadogSession)
        }
      }
      if (
        res.encodingValidationResponse.configuration?.details.filter((conf) => conf.identifierType === 'NFC_TAG')
          .length === 1
      ) {
        if (res.encodingValidationResponse.item.identifiers.filter((id) => id.type === 'NFC_TAG').length > 1) {
          if (
            res.encodingValidationResponse.item.identifiers.some(
              (id) => id.type === 'NFC_TAG' && id.status === 'CONFIRMED'
            ) &&
            res.encodingValidationResponse.item.identifiers.some((id) => id.type === 'NFC_TAG' && id.status === 'ERROR')
          ) {
            res.encodingValidationResponse.item.identifiers = res.encodingValidationResponse.item.identifiers.filter(
              (id) => id.type !== 'NFC_TAG' || id.status !== 'ERROR'
            )
          }
        }
      }
      if (this.state.associationStatus === 'CONFIRMED') {
        this.isEncoding = false
        return
      }
      this.setState(
        {
          encodingValidationResponse: res.encodingValidationResponse,
          encodingValidation: res.encodingValidation
        },
        this.onTagReadValidateResponse
      )
    } catch (error) {
      this.encodingError(error, 'Unknown error during encoding validation')
    } finally {
      this.isEncoding = false
    }
  }

  // callback response validate on flow product and order
  onTagReadValidateResponse = async () => {
    const { encodingValidation, encodingValidationResponse, encodingCreateCounter, associationStatus } = this.state

    if (encodingValidationResponse) {
      if (encodingValidationResponse.operationToPerform === 'NONE') {
        await this.onValidationOperationNone()
      } else if (
        encodingValidationResponse.operationToPerform === 'UPDATE_ONLY_PRODUCT' ||
        encodingValidationResponse.operationToPerform === 'UPDATE_WAM'
      ) {
        const ok = await askUserConfirmation(__(T.misc.attention), __(T.misc.update_only_product))
        if (ok) {
          this.setState({ encodingCreateCounter: encodingCreateCounter + 1 }, this.create)
        } else {
          if (getAskUserInputEveryTime(this.operation)) {
            await this.stopAntenna()
          }
          // this.clear()
          this.setState({ associationStatus: 'ERROR' })
          this.isEncoding = false
        }
      } else if (
        encodingValidationResponse.operationToPerform === 'FAIL' ||
        encodingValidationResponse.operationToPerform.startsWith('FAIL_')
      ) {
        // this.setState({ associationStatus: 'ERROR' })
        // this.feedbackSoundKo()
        const immediateError = encodingValidationResponse.errors.some((e) => e.errorCode === 'MISSING_SYSTEM_TAG')
        this.isEncoding = false
        this.setState({ processing: false, associationStatus: immediateError ? 'ERROR' : associationStatus }, () => {
          if (!immediateError) {
            this.attachAutomaticReaderStop()
          } else {
            if (!encodingValidation || !encodingValidation.identifiers) return
            const tmpEncodingValidation = { ...encodingValidation }
            tmpEncodingValidation.identifiers.forEach((element) => {
              if (element.identifierType === 'UHF_TAG' && !element._status) {
                element._status = 'SKIPPED'
              }
            })
            this.setState({ encodingValidation: tmpEncodingValidation }, () => this.stopAntenna())
          }
        })
      } else if (
        encodingValidationResponse.operationToPerform.startsWith('FORCE_') ||
        encodingValidationResponse.operationToPerform === 'REWRITE_CONFIGURATION' ||
        encodingValidationResponse.operationToPerform === 'UPDATE_ITEM'
      ) {
        this.clearTimerReader()
        this.stopAntenna()
        this.checkEncodingForceOption()
      } else {
        this.setState({ encodingCreateCounter: encodingCreateCounter + 1 }, this.create)
      }
    } else {
      this.feedbackSoundKo()
      this.setState({ processing: false })
      this.isEncoding = false
    }
  }

  retry = async () => this.clearReads()

  clearReads = async (addtags = false, noask = true) => {
    const { encodingValidation, resetFormCounter, targetTag } = this.state
    this.clearTimer()
    RfidReader.clear()
    if (addtags && encodingValidation) {
      RfidReader.tags = encodingValidation.identifiers
        .filter((idf) => !!idf.code)
        .map((idf) => ({ epc: idf.code, tid: idf.code }) as any)
    }

    if (getAskUserInputEveryTime(this.operation) && !noask) {
      RfidReader.setAutomaticStop(false)
      await this.stopAntenna()
      this.setState(
        {
          formSchemaData: { product: { code: '' }, wam: '' },
          associationStatus: 'TO_BE_READ',
          tagsRead: [],
          resetFormCounter: resetFormCounter + 1,
          pin: '',
          forceAssociation: false,
          encodingCreateCounter: 0,
          numWriteAttempt: 0,
          targetTag: targetTag ?? '',
          serial: '',
          processing: false,
          currentTagType: this.tagTypesToCheck[0]
        },
        this.resetValidateResponse
      )
    } else {
      RfidReader.setAutomaticStop(this.operation.autostopAntennaTimeout > 0)
      if ((this.state.formSchemaData.product.code as string).trim().length === 0) return
      const res = await EncodingExtensions.getItemConfiguration(this.operation, this.state.formSchemaData)
      // keep serial
      const serial = encodingValidation?.identifiers.find((idf) => idf.identifierType === 'SIMPLE_ITEM_IDENTIFIER')
      if (serial) serial._status = 'SKIPPED'

      this.setState(
        {
          encodingValidation: {
            ...res.encodingValidation,
            identifiers: [
              ...res.encodingValidation.identifiers
                .filter((idf) => idf.identifierType !== 'SIMPLE_ITEM_IDENTIFIER')
                .concat(serial ? [serial] : [])
            ]
          },
          encodingValidationResponse: res.encodingValidationResponse,
          counters: res.counters,
          encodingCreateResponse: undefined,
          associationStatus: 'TO_BE_READ',
          tagsRead: [],
          pin: '',
          forceAssociation: false,
          encodingCreateCounter: 0,
          numWriteAttempt: 0,
          targetTag: targetTag ?? '',
          serial: '',
          processing: false,
          currentTagType: this.tagTypesToCheck[0]
        },
        async () => {
          this.isWrongTag = false
          await RfidReader.stopWsCustomCommands()
          if (!addtags) {
            await this.startAntenna()
          }
        }
      )
    }
  }

  resetAfterCreate = async () => {
    const serial = (this.state.encodingValidationResponse?.item.identifiers ?? []).find(
      (id) => id.type === 'SIMPLE_ITEM_IDENTIFIER'
    )
    if (serial) {
      localStorage.setItem(`rfid-station::op_${this.operation.id}_custom_lvBrowserPrintLastPairedSerial`, serial.code)
      this.setState({ lastPairedSerial: serial.code })
    }
    this.onClear(true)
  }

  getFullWidthAssociationBox() {
    return true
  }

  _changeTab = async () => {
    localStorage.setItem('originTemplatePath', this.operation.templatePath)
    RfidReader.clear()
    await this.stopAntenna()
    this.timer = setTimeout(() => {
      Router.navigate(
        `/encoding/:opCode/verify`,
        {
          opCode: this.operation.code
        },
        {
          state: {
            originTemplatePath: this.operation.templatePath
          }
        }
      )
    }, this.operation.options.antennaTurnWaitingInterval ?? 1000)
  }

  getDisableRetry() {
    return false
  }

  getHideIdentifierCodeByType = (): IdentifierType[] | undefined => {
    return ['NFC_TAG']
  }

  getCustomSidebarBottomComponent = () => {
    const { lastPairedSerial, formSchemaData } = this.state
    if (!lastPairedSerial || formSchemaData?.product?.code) return null
    return (
      <Box mb={10} mt={16} mh={2}>
        <Text bold data-testid="lv-browser-print-last-paired-serial">
          {__(T.misc.last_paired_sn)}: {lastPairedSerial}
        </Text>
      </Box>
    )
  }

  render() {
    return super.render()
  }
}
